import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Email, Refresh, Check } from "@mui/icons-material";
import { API_URLS } from "../../config/environment";

const VerifyCodeForm = ({ open, onClose, email, onVerificationSuccess }) => {
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const inputRefs = useRef([]);

  // Countdown para reenvío de código
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Función para manejar cambios de código con auto-verificación
  const handleCodeChange = (index, value) => {
    // Solo permitir números
    if (value && !/^\d$/.test(value)) return;

    // Limpiar error cuando el usuario empieza a escribir
    if (error) {
      setError("");
    }

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Verificar automáticamente si completamos el código
    if (value && index === 5) {
      // Es el último dígito y tiene valor
      const fullCode = newCode.join("");
      
      // Verificar automáticamente después de un breve delay para mejor UX
      setTimeout(() => {
        handleSubmit(fullCode);
      }, 300);
      return; // No hacer auto-focus si es el último dígito
    }

    // Auto-focus al siguiente campo si escribimos algo y no es el último
    if (value && index < 5) {
      // Enfocar directamente el elemento input dentro del TextField
      setTimeout(() => {
        const nextTextField = inputRefs.current[index + 1];
        if (nextTextField) {
          const inputElement = nextTextField.querySelector("input");
          if (inputElement) {
            inputElement.focus();
            inputElement.select();
          } else {
            nextTextField.focus();
          }
        }
      }, 10);
    }
  };

  // Función para manejar input directo con reemplazo automático
  const handleInput = (index, e) => {
    const value = e.target.value;

    // Si ya hay un valor en la casilla y escribimos algo nuevo
    if (code[index] && value.length > 1) {
      // Tomar solo el último carácter (el nuevo que escribió)
      const newChar = value.slice(-1);

      if (newChar && /^\d$/.test(newChar)) {
        handleCodeChange(index, newChar);
        return;
      }
    }

    // Comportamiento normal para casillas vacías
    const lastChar = value.slice(-1);

    // Solo procesar si es un número
    if (lastChar && /^\d$/.test(lastChar)) {
      handleCodeChange(index, lastChar);
    } else if (value === "") {
      // Permitir borrar
      handleCodeChange(index, "");
    }
  };

  // Función para manejar cuando el usuario hace focus en un campo
  const handleFocus = (index, e) => {
    // Si la casilla ya tiene un valor, seleccionarlo para facilitar el reemplazo
    if (code[index]) {
      e.target.select();
    }
  };

  // Manejo de teclas con backspace funcional
  const handleKeyDown = (index, e) => {
    // Manejar backspace para ir al campo anterior
    if (e.key === "Backspace") {
      // Si la casilla actual está vacía, ir al campo anterior
      if (!code[index] && index > 0) {
        setTimeout(() => {
          const prevTextField = inputRefs.current[index - 1];
          if (prevTextField) {
            const inputElement = prevTextField.querySelector("input");
            if (inputElement) {
              inputElement.focus();
              inputElement.select();
            } else {
              prevTextField.focus();
            }
          }
        }, 0);
      }
    }

    // Manejar Enter para enviar si el código está completo
    if (e.key === "Enter" && code.every((digit) => digit !== "")) {
      handleSubmit();
    }
  };

  // Función para limpiar todas las casillas (usada en error)
  const clearAllFields = () => {
    setCode(["", "", "", "", "", ""]);
    setTimeout(() => inputRefs.current[0]?.focus(), 100);
  };

  // Paste mejorado con auto-llenado
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text").replace(/\D/g, "");

    if (pastedText.length >= 1) {
      const newCode = ["", "", "", "", "", ""];

      // Llenar hasta 6 dígitos o hasta donde alcance el texto pegado
      for (let i = 0; i < Math.min(6, pastedText.length); i++) {
        newCode[i] = pastedText[i];
      }

      setCode(newCode);

      // Focus en la última casilla llenada o en la primera vacía
      const lastFilledIndex = Math.min(5, pastedText.length - 1);
      const nextEmptyIndex = newCode.findIndex((digit) => digit === "");

      if (nextEmptyIndex !== -1) {
        setTimeout(() => inputRefs.current[nextEmptyIndex]?.focus(), 10);
      } else {
        setTimeout(() => inputRefs.current[lastFilledIndex]?.focus(), 10);
      }
    }
  };

  const handleSubmit = async (paramCode) => {
    const fullCode = paramCode || code.join("");

    if (fullCode.length !== 6) {
      setError("Ingresa el código completo de 6 dígitos");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(
        `${API_URLS.VERIFY_CODE}?email=${encodeURIComponent(
          email
        )}&verifyCode=${fullCode}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        setSuccess(true);

        // Esperar un momento para mostrar el éxito y luego cerrar
        setTimeout(() => {
          onVerificationSuccess();
          handleClose();
        }, 1500);
      } else {
        setError(result.message || "Código inválido o expirado");
        // Limpiar todas las casillas cuando hay error
        setTimeout(clearAllFields, 1000);
      }
    } catch (error) {
      setError("Error de conexión. Intenta nuevamente.");
      // Limpiar todas las casillas cuando hay error
      setTimeout(clearAllFields, 1000);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResendLoading(true);
    setError("");

    try {
      const response = await fetch(
        `${API_URLS.SEND_VERIFY_CODE}?email=${encodeURIComponent(email)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        setResendCooldown(60); // 60 segundos de cooldown
        // Limpiar código actual
        clearAllFields();
      } else {
        setError(result.message || "Error al reenviar el código");
      }
    } catch (error) {
      setError("Error de conexión al reenviar código");
    } finally {
      setResendLoading(false);
    }
  };

  const handleClose = () => {
    setCode(["", "", "", "", "", ""]);
    setError("");
    setSuccess(false);
    setLoading(false);
    onClose();
  };

  // Focus automático al primer campo cuando se abre el dialog
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 100);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Email color="primary" />
          <Typography variant="h6">Verificar Email</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Enviamos un código de verificación a {email}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {success ? (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <Check
              sx={{
                fontSize: 64,
                color: "success.main",
                mb: 2,
              }}
            />
            <Typography variant="h6" color="success.main">
              ¡Email verificado exitosamente!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Redirigiendo...
            </Typography>
          </Box>
        ) : (
          <Box sx={{ py: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Typography variant="body1" sx={{ mb: 3, textAlign: "center" }}>
              Ingresa el código de 6 dígitos que recibiste por email
            </Typography>

            {/* Campos para el código */}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                justifyContent: "center",
                mb: 3,
              }}
              onPaste={handlePaste}
            >
              {code.map((digit, index) => (
                <TextField
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  value={digit}
                  onInput={(e) => handleInput(index, e)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onFocus={(e) => handleFocus(index, e)}
                  variant="outlined"
                  inputProps={{
                    maxLength: 1,
                    style: {
                      textAlign: "center",
                      fontSize: 24,
                      fontWeight: "bold",
                    },
                  }}
                  sx={{
                    width: 56,
                    height: 56,
                    "& .MuiOutlinedInput-root": {
                      height: "100%",
                    },
                  }}
                  disabled={loading || success}
                />
              ))}
            </Box>

            {/* Información sobre expiración */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", textAlign: "center", mb: 2 }}
            >
              El código expira en 10 minutos
            </Typography>

            {/* Botón para reenviar código */}
            <Box sx={{ textAlign: "center" }}>
              <Button
                onClick={handleResendCode}
                disabled={
                  resendLoading || resendCooldown > 0 || loading || success
                }
                startIcon={
                  resendLoading ? <CircularProgress size={16} /> : <Refresh />
                }
                variant="text"
                size="small"
              >
                {resendCooldown > 0
                  ? `Reenviar en ${resendCooldown}s`
                  : resendLoading
                  ? "Reenviando..."
                  : "Reenviar código"}
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        {!success && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || code.some((digit) => digit === "")}
            startIcon={loading && <CircularProgress size={16} />}
            sx={{ minWidth: 120 }}
          >
            {loading ? "Verificando..." : "Verificar"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default VerifyCodeForm;
